// ─────────────────────────────────────────────────────────────────
// Glass-i — Stage 6B.8A-3C: Simplified product creation — helpers.
//
// Pure helpers that the popup modals call. No DOM / React / axios at
// the top of the file (only the save orchestration imports itemsApi
// at runtime). Three responsibilities:
//
//   1. validateSimpleCreate(kind, values)  → array of friendly error
//      messages. Returns [] if valid.
//
//   2. buildPayloadsFor(kind, values)      → { core, extension }
//      Where:
//        core      — keys accepted by POST /api/erp/masters/items
//        extension — keys accepted by PUT /:id/<kind>-spec
//      Both are stripped of empty strings; flags + glass-color
//      preservation already applied. The Advanced Studio's existing
//      `build*Payload` helpers handle the final key allowlist filter.
//
//   3. saveSimpleItem(kind, values)        → orchestrates the two
//      API calls. Returns { item, extensionOk, extensionError }.
//
// Why we split (1) and (2) from (3): the first two are unit-testable
// without an HTTP client. (3) is exercised manually in the modal and
// is intentionally thin (no business rules — just core POST → ext
// PUT, with the extension warning if PUT fails after POST succeeded).
// ─────────────────────────────────────────────────────────────────

import {
  buildCoreItemPayload,
  buildGlassSpecPayload,
  buildProfileSpecPayload,
  buildHardwareSpecPayload,
  buildJobworkSpecPayload,
  buildEcommerceProfilePayload,
  isValidSlug
} from './studioConstants.js';
import {
  SIMPLE_KINDS,
  DEFAULT_FLAGS_BY_KIND,
  GLASS_CATEGORY_OPTIONS,
  PROFILE_MATERIAL_OPTIONS,
  JOBWORK_SERVICE_OPTIONS,
  JOBWORK_CHARGE_BASIS_OPTIONS
} from './simpleCreateConstants.js';

// ── Small primitives ─────────────────────────────────────────────
function isPositive(n)        { return typeof n === 'number' && Number.isFinite(n) && n >= 0; }
function isInRange(n, lo, hi) { return typeof n === 'number' && Number.isFinite(n) && n >= lo && n <= hi; }
function nonEmpty(s)          { return typeof s === 'string' && s.trim().length > 0; }

// ── Validation ───────────────────────────────────────────────────
//
// Returns an array of friendly messages. Empty array means valid.
// Messages are user-facing — no backend codes, no field paths.
export function validateSimpleCreate(kind, values) {
  const out = [];
  const v = values || {};

  // Common across all kinds — name + GST + price + thickness sanity.
  if (kind === SIMPLE_KINDS.JOBWORK) {
    if (!nonEmpty(v.itemName)) out.push('Service Name is required.');
  } else if (kind === SIMPLE_KINDS.ECOMMERCE) {
    if (!nonEmpty(v.itemName)) out.push('Product Name is required.');
  } else {
    if (!nonEmpty(v.itemName)) out.push('Product Name is required.');
  }

  if (v.gstRatePercent != null && !isInRange(Number(v.gstRatePercent), 0, 100)) {
    out.push('GST (%) must be a number between 0 and 100.');
  }
  if (v.salesRate != null && Number(v.salesRate) < 0) {
    out.push('Selling Price cannot be negative.');
  }
  if (v.mrp != null && Number(v.mrp) < 0) {
    out.push('MRP cannot be negative.');
  }
  if (v.thicknessMm != null && Number(v.thicknessMm) < 0) {
    out.push('Thickness cannot be negative.');
  }

  // Kind-specific rules.
  if (kind === SIMPLE_KINDS.GLASS) {
    // The brief makes Stock Name / Base Material required for Glass.
    // The user can either pick an existing stock item (gives us
    // sourceStockItemId) OR type free text (gives us stockDesc).
    if (!v.sourceStockItemId && !nonEmpty(v.stockDesc)) {
      out.push('Stock Name / Base Material is required.');
    }
  }

  if (kind === SIMPLE_KINDS.ECOMMERCE) {
    if (v.ecommerceSlug && !isValidSlug(v.ecommerceSlug)) {
      out.push('Slug must be lowercase letters, digits, and dashes only (e.g. clear-glass-4mm).');
    }
    // Backend custom() guard: publish=published requires slug.
    if (v.publishStatus === 'published' && !nonEmpty(v.ecommerceSlug)) {
      out.push('A slug is required before publishing.');
    }
  }

  return out;
}

// ── Payload builders ─────────────────────────────────────────────
//
// The popup form binds friendly labels (Selling Price, GST (%), …).
// Those are already on the camelCase keys the backend expects
// (salesRate, gstRatePercent, etc.) thanks to the Form's `name` prop.
// This helper:
//   1. Merges the kind's default flags onto the form values
//      (without overwriting anything the user explicitly set).
//   2. Splits the merged object into { core, extension } via the
//      existing studioConstants build* helpers — which already
//      enforce the backend's allowed-key list and strip empty
//      strings.
//   3. Glass-only: maps the friendly "Category" label onto the
//      glassFamily enum AND preserves the original label in
//      glassColor for human-readable filtering later.
//   4. Profile-only: maps the friendly "Profile Material" label
//      onto profileMaterial + isAluminium/isUpvc/isSlimProfile
//      flags so reports can group correctly.
//   5. Jobwork-only: maps the friendly "Service Type" label onto
//      jobworkType + jobworkCategory; maps "Charge Basis" onto
//      chargeBasis + rateBasis.
//   6. Ecommerce-only: forces is_visible_online=false at create
//      time unless the user has explicitly toggled visibility
//      AND status to public/published. (Mirrors the brief.)
export function buildPayloadsFor(kind, values) {
  const v = values || {};
  const defaults = DEFAULT_FLAGS_BY_KIND[kind] || {};
  // Layer order: defaults < form values. Never let defaults silently
  // overwrite an explicit user choice.
  const merged = { ...defaults, ...v };

  if (kind === SIMPLE_KINDS.GLASS) {
    return splitGlassPayload(merged);
  }
  if (kind === SIMPLE_KINDS.HARDWARE) {
    return splitHardwarePayload(merged);
  }
  if (kind === SIMPLE_KINDS.PROFILE) {
    return splitProfilePayload(merged);
  }
  if (kind === SIMPLE_KINDS.JOBWORK) {
    return splitJobworkPayload(merged);
  }
  if (kind === SIMPLE_KINDS.ECOMMERCE) {
    return splitEcommercePayload(merged);
  }
  // Unknown kind → only the core payload, no extension. The modal
  // layer never calls this with an unknown kind, but keep the
  // function total.
  return { core: buildCoreItemPayload(merged), extension: null };
}

function splitGlassPayload(v) {
  // 1. Map "Category" label → glassFamily + preserve the original
  //    label in glassColor so the user can still see it later.
  //
  //    Stage 6B.8A-3N: the Category dropdown is now DB-driven via the
  //    Glass Category Master. The modal writes the picked row's
  //    glassFamily/glassColor onto the form values directly, so we
  //    only fall back to the seed lookup when those weren't supplied
  //    (e.g. tests, programmatic callers, server-side edit flows).
  let extra = {};
  if (typeof v.glassCategoryLabel === 'string' && v.glassCategoryLabel) {
    const match = GLASS_CATEGORY_OPTIONS.find(
      (o) => o.label === v.glassCategoryLabel
    );
    if (match) {
      if (!v.glassFamily) extra.glassFamily = match.glassFamily;
      if (!v.glassColor)  extra.glassColor  = match.glassColor;
    }
  }
  // 2. Brand has no dedicated column on item_glass_specs — preserve
  //    it on the spec's `remarks` field if the user typed one.
  if (nonEmpty(v.brand)) {
    extra.remarks = extra.remarks
      ? `Brand: ${v.brand}. ${extra.remarks}`
      : `Brand: ${v.brand}`;
  }
  const extInput = { ...v, ...extra };
  // glassCategoryLabel is a popup-only field — it must NOT reach the
  // backend, so we delete it before building the core payload.
  const coreInput = { ...v };
  delete coreInput.glassCategoryLabel;
  delete coreInput.brand;

  return {
    core:      buildCoreItemPayload(coreInput),
    extension: buildGlassSpecPayload(extInput)
  };
}

function splitHardwarePayload(v) {
  // No friendly-label remapping needed — Hardware popup binds to
  // the backend names directly (hardwareType, modelNo, color, etc.).
  return {
    core:      buildCoreItemPayload(v),
    extension: buildHardwareSpecPayload(v)
  };
}

function splitProfilePayload(v) {
  let extra = {};
  if (typeof v.profileMaterialLabel === 'string' && v.profileMaterialLabel) {
    const match = PROFILE_MATERIAL_OPTIONS.find(
      (o) => o.label === v.profileMaterialLabel
    );
    if (match) {
      extra.profileMaterial = match.value;
      if (match.value === 'aluminium')      extra.isAluminium    = true;
      if (match.value === 'upvc')           extra.isUpvc         = true;
      if (match.isSlimProfile === true)     extra.isSlimProfile  = true;
    }
  }
  const coreInput = { ...v };
  delete coreInput.profileMaterialLabel;
  return {
    core:      buildCoreItemPayload(coreInput),
    extension: buildProfileSpecPayload({ ...v, ...extra })
  };
}

function splitJobworkPayload(v) {
  let extra = {};
  if (typeof v.serviceTypeLabel === 'string' && v.serviceTypeLabel) {
    const match = JOBWORK_SERVICE_OPTIONS.find(
      (o) => o.label === v.serviceTypeLabel
    );
    if (match) {
      extra.jobworkType     = match.jobworkType;
      extra.jobworkCategory = match.jobworkCategory;
    }
  }
  if (typeof v.chargeBasisLabel === 'string' && v.chargeBasisLabel) {
    const match = JOBWORK_CHARGE_BASIS_OPTIONS.find(
      (o) => o.label === v.chargeBasisLabel
    );
    if (match) {
      extra.chargeBasis = match.value;
      extra.rateBasis   = match.rateBasis;
    }
  }
  const coreInput = { ...v };
  delete coreInput.serviceTypeLabel;
  delete coreInput.chargeBasisLabel;
  return {
    core:      buildCoreItemPayload(coreInput),
    extension: buildJobworkSpecPayload({ ...v, ...extra })
  };
}

function splitEcommercePayload(v) {
  // Stage 6B.8A-3C brief: e-commerce items stay hidden + draft +
  // not-visible-online unless the user explicitly opts in. Only
  // promote isVisibleOnline=true when both visibility=public AND
  // publish_status=published. Otherwise force the safe state.
  const visiblePublic   = v.visibility === 'public';
  const publishedStatus = v.publishStatus === 'published';
  const visible         = visiblePublic && publishedStatus
    ? (v.isVisibleOnline === undefined ? true : Boolean(v.isVisibleOnline))
    : false;
  const safeVisibility   = visiblePublic   ? 'public'    : 'hidden';
  const safePublish      = publishedStatus ? 'published' : 'draft';
  return {
    core:      buildCoreItemPayload(v),
    extension: buildEcommerceProfilePayload({
      ...v,
      visibility:      safeVisibility,
      publishStatus:   safePublish,
      isVisibleOnline: visible
    })
  };
}

// The save orchestration lives in ./simpleCreateSave.js — it imports
// itemsApi (which transitively imports the axios client that touches
// window.sessionStorage). Keeping it in a separate file lets this
// pure-helpers module be exercised from node:test without crashing
// on `window is not defined`.

// ── Map of kind → extension-upsert method name (used by save) ───
export const SPEC_UPSERT_METHOD_BY_KIND = Object.freeze({
  [SIMPLE_KINDS.GLASS]:     'upsertGlassSpec',
  [SIMPLE_KINDS.HARDWARE]:  'upsertHardwareSpec',
  [SIMPLE_KINDS.PROFILE]:   'upsertProfileSpec',
  [SIMPLE_KINDS.JOBWORK]:   'upsertJobworkSpec',
  [SIMPLE_KINDS.ECOMMERCE]: 'upsertEcommerceProfile'
});

// ── Friendly extension-failure section name (used by the modal) ──
export const EXTENSION_LABEL_BY_KIND = Object.freeze({
  [SIMPLE_KINDS.GLASS]:     'glass',
  [SIMPLE_KINDS.HARDWARE]:  'hardware',
  [SIMPLE_KINDS.PROFILE]:   'profile',
  [SIMPLE_KINDS.JOBWORK]:   'jobwork',
  [SIMPLE_KINDS.ECOMMERCE]: 'ecommerce'
});
